/**
 * Browser client for the Builder product tile and Featured Product Slider.
 *
 * ONE same-origin read of `/resources/featured-products` per block, primaries
 * and backups together (see the route for why a batch). Never from a loader.
 * Returns a result object; never throws.
 *
 * Signals are linked by hand rather than with `AbortSignal.any`, which Safari
 * < 17.4 lacks — a throw there would blank every tile with nothing logged
 * (same reason as `use-quick-view-product.ts`).
 */
import { upstreamSignal } from "~/utils/upstream-timeouts";

import { scapiProductToTileProduct } from "./dynamic-product-tile.logic";
import { featuredProductsResponseSchema } from "./dynamic-product-tile.schemas";
import type { FeaturedProductsResult } from "./types";

/**
 * Statuses worth one more try: the route maps SCAPI 5xx / 429 / timeout here.
 * Exported so the route's test pins both sides of that contract.
 */
export const RETRYABLE_STATUS = new Set([502, 503, 504]);

function linkSignals(a: AbortSignal, b: AbortSignal | undefined): AbortSignal {
  if (!b) return a;
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (a.aborted || b.aborted) {
    abort();
  } else {
    a.addEventListener("abort", abort, { once: true });
    b.addEventListener("abort", abort, { once: true });
  }
  return controller.signal;
}

async function attempt(
  href: string,
  signal: AbortSignal | undefined,
): Promise<FeaturedProductsResult | "retry"> {
  try {
    const response = await fetch(href, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal: linkSignals(upstreamSignal("scapi"), signal),
    });
    if (!response.ok) {
      return RETRYABLE_STATUS.has(response.status)
        ? "retry"
        : { kind: "error" };
    }
    const parsed = featuredProductsResponseSchema.safeParse(
      (await response.json()) as unknown,
    );
    if (!parsed.success) return { kind: "error" };
    return {
      kind: "ok",
      products: parsed.data.products.map(scapiProductToTileProduct),
    };
  } catch {
    // Our own abort (unmount / ids changed) is final; a network failure or
    // timeout gets the one retry.
    return signal?.aborted ? { kind: "error" } : "retry";
  }
}

export async function fetchFeaturedProducts(
  href: string,
  signal?: AbortSignal,
): Promise<FeaturedProductsResult> {
  const first = await attempt(href, signal);
  if (first !== "retry") return first;
  const second = await attempt(href, signal);
  return second === "retry" ? { kind: "error" } : second;
}
